import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {createTag, loadTags, setCandidateTags} from './Api'
import {ITagModel, TAG_COLORS, tagTextColor} from './TagModel'
import {TagFormFields} from './TagFormFields'

export interface ITagTarget {
  candidateId: string
  candidateName: string
  tags: ITagModel[]
}

type Props = {
  target: ITagTarget | null
  onClose: () => void
  /** Etiketler kaydedilince (liste yenilenir) */
  onSaved: () => void
}

/** Adayı etiketleme: mevcut etiketleri seç / kaldır, gerekirse yeni etiket oluştur */
export const TagAssignModal: FC<Props> = ({target, onClose, onSaved}) => {
  const [allTags, setAllTags] = useState<ITagModel[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(TAG_COLORS[3])

  useEffect(() => {
    if (!target) return
    setSelectedIds(target.tags.map((tag) => tag.id))
    setCreating(false)
    setNewName('')
    loadTags()
      .then(({data}) => setAllTags(data.payload))
      .catch((error) => showErrorToast(apiErrorMessage(error, 'Etiketler yüklenemedi')))
  }, [target])

  const toggle = (tagId: string) =>
    setSelectedIds((ids) => (ids.includes(tagId) ? ids.filter((id) => id !== tagId) : [...ids, tagId]))

  const handleCreate = async () => {
    if (!newName.trim()) return
    try {
      const {data} = await createTag({name: newName.trim(), color: newColor})
      setAllTags((tags) => [...tags, data.payload].sort((a, b) => a.name.localeCompare(b.name, 'tr')))
      setSelectedIds((ids) => [...ids, data.payload.id])
      setCreating(false)
      setNewName('')
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Etiket oluşturulamadı'))
    }
  }

  const handleSave = async () => {
    if (!target) return
    setSaving(true)
    try {
      await setCandidateTags(target.candidateId, selectedIds)
      showSuccessToast('Etiketler kaydedildi')
      onSaved()
      onClose()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Etiketler kaydedilemedi'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={!!target} onHide={onClose} dialogClassName='modal-dialog-centered'>
      <div className='modal-header'>
        <h5 className='modal-title'>{target?.candidateName} · Etiketle</h5>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>
      <div className='modal-body'>
        <div className='text-muted fs-7 mb-4'>Seçmek veya kaldırmak için etikete tıklayın.</div>
        <div className='d-flex flex-wrap gap-2 mb-6'>
          {allTags.length === 0 && <span className='text-muted'>Henüz etiket yok; aşağıdan oluşturabilirsiniz.</span>}
          {allTags.map((tag) => {
            const selected = selectedIds.includes(tag.id)
            return (
              <button
                key={tag.id}
                type='button'
                className='btn btn-sm py-2 px-3 d-inline-flex align-items-center gap-2'
                style={
                  selected
                    ? {backgroundColor: tag.color, color: tagTextColor(tag.color), border: `1px solid ${tag.color}`}
                    : {backgroundColor: 'transparent', color: '#3f4254', border: `1px dashed ${tag.color}`}
                }
                onClick={() => toggle(tag.id)}
              >
                <i className={`bi ${selected ? 'bi-check-lg' : 'bi-plus-lg'}`}></i>
                {tag.name}
              </button>
            )
          })}
        </div>

        {creating ? (
          <div className='border rounded p-5'>
            <TagFormFields
              autoFocus
              name={newName}
              color={newColor}
              onNameChange={setNewName}
              onColorChange={setNewColor}
            />
            <div className='d-flex justify-content-end gap-2 mt-5'>
              <button type='button' className='btn btn-sm btn-light' onClick={() => setCreating(false)}>
                Vazgeç
              </button>
              <button type='button' className='btn btn-sm btn-primary' disabled={!newName.trim()} onClick={handleCreate}>
                Oluştur ve Seç
              </button>
            </div>
          </div>
        ) : (
          <button type='button' className='btn btn-sm btn-light-primary' onClick={() => setCreating(true)}>
            <i className='bi bi-plus-lg'></i>
            Yeni Etiket
          </button>
        )}
      </div>
      <div className='modal-footer'>
        <button type='button' className='btn btn-light' onClick={onClose}>
          İptal
        </button>
        <button type='button' className='btn btn-primary' disabled={saving} onClick={handleSave}>
          {saving ? <span className='spinner-border spinner-border-sm' /> : 'Kaydet'}
        </button>
      </div>
    </Modal>
  )
}

/** Satırdaki "x" ile tek etiket kaldırma */
export async function removeCandidateTag(target: ITagTarget, tag: ITagModel) {
  await setCandidateTags(
    target.candidateId,
    target.tags.filter((item) => item.id !== tag.id).map((item) => item.id)
  )
}
